<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <div ref="container" class="activity-container" @click="onActivityClick">
    <PrimeDataView
      :value="groupedActivities"
      paginator
      :rows="visibleRows"
      layout="list"
    >
      <template #empty>
        {{ $t("emptyActivityHistory") }}
      </template>
      <template #list="slotProps">
        <div>
          <div
            v-for="group in slotProps.items"
            :key="group.entry.id"
            class="activity-row"
          >
            <i
              :class="getIcon(group.entry.activityType)"
              class="activity-icon"
              aria-hidden="true"
            ></i>

            <div class="activity-content">
              <p class="activity-text">
                <strong
                  v-tooltip.bottom="group.entry.postPersonId"
                  class="cursor-default"
                >
                  {{ getUserDisplayName(group.entry) }}
                </strong>
                <!-- eslint-disable-next-line vue/no-v-html -- Activity messages contain safe HTML with escaped user content -->
                <span v-html="' ' + getActivityMessageHtml(group.entry)"></span>
                <span v-if="group.count > 1" class="activity-count">
                  (+{{ group.count - 1 }})
                </span>
              </p>
              <p class="activity-date">
                {{ formatDateTime(group.entry.postedAt) }}
              </p>
            </div>
          </div>
        </div>
      </template>
    </PrimeDataView>
  </div>
</template>

<script>
import { activitiesApi } from "@/AlfrescoApi.js";
import formatService from "@/services/FormatService.js";
import { useConfigStore } from "@/stores/config";
import { mapState } from "pinia";
import DataView from "primevue/dataview";
import Tooltip from "primevue/tooltip";

export default {
  name: "ActivityHistory",
  components: {
    PrimeDataView: DataView,
  },
  directives: {
    tooltip: Tooltip,
  },
  props: {
    urlResolver: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      activities: null,
      visibleRows: 5,
      resizeObserver: null,
    };
  },
  computed: {
    ...mapState(useConfigStore, ["ESPACES_HOST"]),
    groupedActivities() {
      if (!this.activities) {
        return [];
      }
      const groups = [];
      for (const activity of this.activities) {
        const prev = groups[groups.length - 1];
        if (prev && this.isSameGroup(prev.entry, activity.entry)) {
          prev.count++;
        } else {
          groups.push({ entry: activity.entry, count: 1 });
        }
      }
      return groups;
    },
  },
  mounted() {
    this.getActivity();
    this.resizeObserver = new ResizeObserver(() => this.computeVisibleRows());
    this.resizeObserver.observe(this.$refs.container);
  },
  beforeUnmount() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    isSameGroup(a, b) {
      return (
        a.postPersonId === b.postPersonId &&
        a.activitySummary?.objectId === b.activitySummary?.objectId &&
        a.activitySummary?.objectId !== undefined
      );
    },
    computeVisibleRows() {
      const container = this.$refs.container;
      if (!container) {
        return;
      }
      const ROW_HEIGHT = 52;
      const PAGINATOR_HEIGHT = 50;
      const available = container.clientHeight - PAGINATOR_HEIGHT;
      const rows = Math.max(3, Math.floor(available / ROW_HEIGHT));
      this.visibleRows = rows;
    },
    reloadData() {
      this.getActivity();
    },
    formatDateTime(date) {
      return formatService.formatDateTime(date);
    },
    getIcon(type) {
      if (!type) {
        return "ri-information-line";
      }

      const exactIconMap = {
        "org.alfresco.documentlibrary.file-downloaded": "ri-download-line",
        "org.alfresco.documentlibrary.file-added": "ri-add-circle-line",
        "org.alfresco.site.user-joined": "ri-user-add-line",
        "org.alfresco.site.user-left": "ri-user-unfollow-line",
        "org.alfresco.site.user-role-changed": "ri-user-settings-line",
        "org.alfresco.calendar.event-created": "ri-calendar-check-line",
        "org.alfresco.calendar.event-updated": "ri-calendar-line",
        "org.alfresco.calendar.event-deleted": "ri-calendar-close-line",
        "org.alfresco.blog.post-created": "ri-article-line",
        "org.alfresco.blog.post-updated": "ri-article-line",
        "org.alfresco.blog.post-deleted": "ri-delete-bin-line",
        "org.alfresco.links.link-created": "ri-link",
        "org.alfresco.links.link-updated": "ri-link",
        "org.alfresco.links.link-deleted": "ri-delete-bin-line",
      };

      if (exactIconMap[type]) {
        return exactIconMap[type];
      }

      if (type.includes("deleted")) {
        return "ri-delete-bin-line";
      }
      if (type.includes("added") || type.includes("created")) {
        return "ri-add-circle-line";
      }
      if (type.includes("updated") || type.includes("edited")) {
        return "ri-edit-line";
      }
      if (type.includes("downloaded")) {
        return "ri-download-line";
      }
      if (type.includes("comment")) {
        return "ri-chat-3-line";
      }
      if (type.includes("discussion") || type.includes("reply")) {
        return "ri-discuss-line";
      }
      if (type.includes("user-joined")) {
        return "ri-user-add-line";
      }
      if (type.includes("user-left")) {
        return "ri-user-unfollow-line";
      }
      if (type.includes("role-changed")) {
        return "ri-user-settings-line";
      }

      return "ri-information-line";
    },
    getUserDisplayName(entry) {
      const summary = entry.activitySummary || {};
      if (
        entry.activityType.includes("user-role-changed") ||
        entry.activityType.includes("user-joined") ||
        entry.activityType.includes("user-left")
      ) {
        const first = summary.memberFirstName || summary.firstName || "";
        const last = summary.memberLastName || summary.lastName || "";
        return (first + " " + last).trim() || entry.postPersonId;
      }
      const first = summary.firstName || "";
      const last = summary.lastName || "";
      return (first + " " + last).trim() || entry.postPersonId;
    },
    getObjectUrl(entry) {
      const summary = entry.activitySummary || {};
      if (!summary.objectId || !this.ESPACES_HOST) {
        return null;
      }
      if (entry.activityType.includes("folder")) {
        return `${this.ESPACES_HOST}/espaces/mes-espaces/dossier/${summary.objectId}`;
      }
      return `${this.ESPACES_HOST}/espaces/${summary.objectId}`;
    },
    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    },
    getActivitiesMessages() {
      // Activity type keys contain dots (e.g. "org.alfresco.documentlibrary.file-added")
      // which $t() interprets as nested path separators.
      // Use $tm() to get the raw message object for the "activities" key.
      const raw = this.$tm("activities");
      if (raw && typeof raw === "object") {
        // $tm() returns reactive message objects; convert values to plain strings
        const messages = {};
        for (const key of Object.keys(raw)) {
          const val = raw[key];
          messages[key] =
            typeof val === "string" ? val : val?.toString?.() || String(val);
        }
        return messages;
      }
      return {};
    },
    getActivityMessageHtml(entry) {
      const summary = entry.activitySummary || {};
      const title = summary.title || "";
      const activities = this.getActivitiesMessages();

      const params = {
        title: title,
        firstName: summary.firstName || "",
        siteName: summary.siteName || "",
        newTitle: summary.newTitle || "",
        role: this.$t(`role.${summary.role}`, summary.role || ""),
        type: entry.activityType,
      };

      let message = activities[entry.activityType] || activities["unknown"];
      if (!message) {
        return this.escapeHtml(entry.activityType);
      }

      // Interpolate all params except title
      Object.keys(params).forEach((key) => {
        if (key === "title") {
          return;
        }
        message = message.replace(
          new RegExp(`\\{${key}\\}`, "g"),
          this.escapeHtml(params[key]),
        );
      });

      // Escape the rest of the message
      // Split around {title} placeholders, escape each part, then rejoin with the link
      if (title) {
        const summary = entry.activitySummary || {};
        const objectId = summary.objectId;
        const escapedTitle = this.escapeHtml(title);
        let titleHtml;
        if (this.urlResolver && objectId) {
          const siteId = entry.siteId || "";
          titleHtml = `<a href="javascript:void(0)" data-object-id="${this.escapeHtml(objectId)}" data-site-id="${this.escapeHtml(siteId)}" data-is-folder="${entry.activityType.includes("folder")}" class="activity-link">${escapedTitle}</a>`;
        } else {
          const url = this.getObjectUrl(entry);
          titleHtml = url
            ? `<a href="${this.escapeHtml(url)}" target="_blank" rel="noopener noreferrer" class="activity-link">${escapedTitle}</a>`
            : `<strong>${escapedTitle}</strong>`;
        }
        const parts = message.split("{title}");
        message = parts.map((p) => this.escapeHtml(p)).join(titleHtml);
      } else {
        message = this.escapeHtml(message);
      }

      return message;
    },
    onActivityClick(event) {
      const link = event.target.closest(".activity-link[data-object-id]");
      if (!link || !this.urlResolver) {
        return;
      }
      event.preventDefault();
      const objectId = link.dataset.objectId;
      const siteId = link.dataset.siteId || "";
      const isFolder = link.dataset.isFolder === "true";
      this.urlResolver({
        id: objectId,
        siteId,
        isFolder,
        forceOverview: isFolder,
      });
    },
    getActivity() {
      let opts = {};
      return activitiesApi
        .listActivitiesForPerson("-me-", opts)
        .then((data) => {
          this.activities = data.list.entries;
        });
    },
  },
};
</script>

<style scoped>
.activity-container {
  height: 100%;
}

.activity-row {
  display: flex;
  gap: 0.75rem;
  padding: 0.5rem 0;
}

.activity-icon {
  font-size: 1.25rem;
  color: var(--p-primary-color);
  margin-top: 0.25rem;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-text {
  font-size: 0.875rem;
  margin: 0;
}

.activity-count {
  margin-left: 0.25rem;
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
}

.activity-date {
  font-size: 0.75rem;
  color: var(--p-text-muted-color);
  margin: 0;
  margin-top: 0.25rem;
}

.cursor-default {
  cursor: default;
}

:deep(.activity-link) {
  color: var(--p-primary-color);
  text-decoration: none;
  font-weight: 600;
}

:deep(.activity-link:hover) {
  text-decoration: underline;
}

:deep(.activity-link:focus-visible) {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 2px;
  border-radius: 2px;
}
</style>
